// Wochenvorschau-Streifen: Ueberfaellig zuerst, dann heute + 6 Folgetage, optional "ohne Termin".
// Geteilt zwischen dem Meister-Dashboard (weekAhead, werksweit) und dem Mechaniker-Home
// (myWeek, "wieviel liegt naechste Woche auf MEINEM Tisch" — REL in den eigenen Arbeitsplaetzen).
// Balken sind handgerollte divs (Lager-Chart-Idiom, keine Chart-Lib); Datum als dd.MM. ohne
// Locale-Abhaengigkeit, der erste Tag heisst "Heute" (i18n).
import React from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { Card } from './ui';

const BAR_MAX = 36; // px — kompakter Streifen, kein grosses Chart

/**
 * @param {string} title — bereits uebersetzte Ueberschrift ("Naechste 7 Tage" / "Meine Woche")
 * @param {{overdue:number, days:Array<{date:string,count:number}>, unscheduled:number, source:string}} week
 * @param {Function} [onClick] — macht den ganzen Streifen klickbar (Drill-down in eine Liste)
 */
function WeekStrip({ title, week, onClick }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  if (!week) return null;

  const mockBadge = isDarkMode
    ? 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 ml-2'
    : 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 ml-2';

  const max = Math.max(week.overdue, ...week.days.map((d) => d.count), 1);
  const barH = (count) => Math.max(Math.round((count / max) * BAR_MAX), count > 0 ? 6 : 2);
  const label = (iso, i) => (i === 0 ? t('pm.week.today') : `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`);

  const cells = [
    { key: 'overdue', label: t('pm.week.overdue'), count: week.overdue, accent: 'overdue' },
    ...week.days.map((d, i) => ({ key: d.date, label: label(d.date, i), count: d.count, accent: 'day' })),
  ];

  const inner = (
    <Card className="space-y-2">
      <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
        {title}
        {week.source === 'mock' && <span className={mockBadge}>{t('pm.dash.mockBadge')}</span>}
      </h3>
      <div className="flex items-end gap-1.5">
        {cells.map((c) => (
          <div key={c.key} className="flex flex-col items-center flex-1 min-w-0">
            <div className={`text-xs font-semibold ${c.count > 0
              ? (c.accent === 'overdue'
                  ? (isDarkMode ? 'text-rose-300' : 'text-rose-600')
                  : (isDarkMode ? 'text-blue-200' : 'text-brand'))
              : (isDarkMode ? 'text-white/25' : 'text-slate-300')
            }`}>
              {c.count}
            </div>
            <div
              className={`w-full rounded-t-md ${c.accent === 'overdue'
                ? (isDarkMode ? 'bg-rose-500/60' : 'bg-rose-400')
                : (isDarkMode ? 'bg-blue-500/60' : 'bg-brand/70')
              } ${c.count === 0 ? 'opacity-30' : ''}`}
              style={{ height: barH(c.count) }}
            />
            <div className={`text-[10px] mt-1 truncate max-w-full ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {c.label}
            </div>
          </div>
        ))}
      </div>
      {week.unscheduled > 0 && (
        <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          + {week.unscheduled} {t('pm.week.unscheduled')}
        </p>
      )}
    </Card>
  );

  return typeof onClick === 'function'
    ? <button onClick={onClick} className="text-left w-full">{inner}</button>
    : inner;
}

export default WeekStrip;
